import { Controller } from "@hotwired/stimulus"
import * as Turbo from "@hotwired/turbo"

// Card #4871 (Inbox phase 1, part B). Bulk select + bulk Done/Snooze/Mute
// across the Inbox's current page of rows.
//
// No <form> wraps the checkboxes: each row also carries its own standalone
// Snooze "pick a date" form (app/views/activities/_row_actions.html.erb),
// and a <form> nested inside another <form> is invalid HTML (the browser
// silently closes the outer one). So bulk submission goes through fetch +
// Turbo.renderStreamMessage instead -- the same approach already used by
// step-checkbox, runbook-graph and review-actions controllers.
export default class extends Controller {
  static targets = ["checkbox", "selectAll", "bar", "count"]
  static values = {
    view: String,
    filters: Array,
    bulkDoneUrl: String,
    bulkSnoozeUrl: String,
    bulkMuteUrl: String
  }

  toggleOne() {
    this.refresh()
  }

  toggleAll(event) {
    const checked = event.target.checked
    this.checkboxTargets.forEach((box) => { box.checked = checked })
    this.refresh()
  }

  bulkDone() {
    this.submit(this.bulkDoneUrlValue, {})
  }

  bulkMute() {
    this.submit(this.bulkMuteUrlValue, {})
  }

  bulkSnoozePreset(event) {
    this.submit(this.bulkSnoozeUrlValue, { preset: event.params.preset })
  }

  bulkSnoozeCustom(event) {
    event.preventDefault()
    const dateField = event.target.querySelector("[name='snooze_date']")
    if (!dateField || !dateField.value) return
    this.submit(this.bulkSnoozeUrlValue, { preset: "custom", snooze_date: dateField.value })
  }

  refresh() {
    const count = this.selectedIds().length
    if (this.hasBarTarget) this.barTarget.classList.toggle("hidden", count === 0)
    if (this.hasCountTarget) this.countTarget.textContent = `${count} selected`
  }

  selectedIds() {
    return this.checkboxTargets.filter((box) => box.checked).map((box) => box.value)
  }

  async submit(baseUrl, extraParams) {
    const ids = this.selectedIds()
    if (ids.length === 0 || !baseUrl) return

    const url = new URL(baseUrl, window.location.origin)
    url.searchParams.set("view", this.viewValue)
    this.filtersValue.forEach((filter) => url.searchParams.append("filters[]", filter))
    ids.forEach((id) => url.searchParams.append("ids[]", id))
    Object.entries(extraParams).forEach(([key, value]) => url.searchParams.set(key, value))

    const response = await fetch(url.toString(), {
      method: "PATCH",
      headers: {
        "X-CSRF-Token": this.csrfToken,
        "Accept": "text/vnd.turbo-stream.html"
      }
    })

    if (response.ok) {
      const html = await response.text()
      Turbo.renderStreamMessage(html)
      this.resetSelection()
    }
  }

  resetSelection() {
    this.checkboxTargets.forEach((box) => { box.checked = false })
    if (this.hasSelectAllTarget) this.selectAllTarget.checked = false
    this.refresh()
  }

  get csrfToken() {
    return document.querySelector('meta[name="csrf-token"]')?.content
  }
}
